import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { API_CONFIG } from '../config/api.config';
import { biometricService, BiometricStatus } from '../services/biometric.service';
import './Login.css';

export const Login: React.FC = () => {
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });
  const navigate = useNavigate();

  // PIN change flow state
  const [showPinChange, setShowPinChange] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [employeeName, setEmployeeName] = useState('');

  // Biometric state
  const [biometricStatus, setBiometricStatus] = useState<BiometricStatus | null>(null);
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState<{ phone: string; pin: string } | null>(null);

  // Check biometric availability on mount
  useEffect(() => {
    setPhone('');
    setPin('');
    setError('');

    // Check biometric status
    const checkBiometrics = async () => {
      const status = await biometricService.getStatus();
      setBiometricStatus(status);

      // If biometrics enabled, try to auto-login
      if (status.isEnabled && status.isAvailable) {
        handleBiometricLogin();
      }
    };
    checkBiometrics();
  }, []);

  const translations = {
    en: {
      signIn: 'Sign in to continue',
      phoneNumber: 'Phone Number',
      pinLabel: 'PIN',
      enterPin: 'Enter 4-digit PIN',
      signInButton: 'Sign In',
      signingIn: 'Signing in...',
      invalidPhone: 'Please enter a valid 10-digit phone number',
      invalidPin: 'PIN must be 4 digits',
      loginFailed: 'Invalid phone number or PIN',
      connectionError: 'Cannot connect to server. Please check your connection.',
      // PIN change translations
      welcomeUser: 'Welcome',
      pinChangeRequired: 'Please create your new PIN',
      newPinLabel: 'New PIN',
      confirmPinLabel: 'Confirm PIN',
      enterNewPin: 'Enter new 4-digit PIN',
      confirmNewPin: 'Confirm your PIN',
      setPinButton: 'Set PIN',
      settingPin: 'Setting PIN...',
      pinMismatch: 'PINs do not match',
      pinSetSuccess: 'PIN set successfully!',
      // Biometric translations
      useBiometric: 'Use',
      orSignInWithPin: 'Or sign in with PIN',
      enableBiometric: 'Enable',
      enableBiometricQuestion: 'Would you like to enable',
      forFasterLogin: 'for faster login?',
      enableButton: 'Enable',
      skipButton: 'Skip',
      biometricFailed: 'Biometric authentication failed'
    },
    es: {
      signIn: 'Inicie sesión para continuar',
      phoneNumber: 'Número de Teléfono',
      pinLabel: 'PIN',
      enterPin: 'Ingrese PIN de 4 dígitos',
      signInButton: 'Iniciar Sesión',
      signingIn: 'Iniciando sesión...',
      invalidPhone: 'Por favor ingrese un número válido de 10 dígitos',
      invalidPin: 'El PIN debe ser de 4 dígitos',
      loginFailed: 'Número de teléfono o PIN inválido',
      connectionError: 'No se puede conectar al servidor. Por favor revise su conexión.',
      // PIN change translations
      welcomeUser: 'Bienvenido',
      pinChangeRequired: 'Por favor cree su nuevo PIN',
      newPinLabel: 'Nuevo PIN',
      confirmPinLabel: 'Confirmar PIN',
      enterNewPin: 'Ingrese nuevo PIN de 4 dígitos',
      confirmNewPin: 'Confirme su PIN',
      setPinButton: 'Establecer PIN',
      settingPin: 'Estableciendo PIN...',
      pinMismatch: 'Los PINs no coinciden',
      pinSetSuccess: '¡PIN establecido exitosamente!',
      // Biometric translations
      useBiometric: 'Usar',
      orSignInWithPin: 'O inicie sesión con PIN',
      enableBiometric: 'Habilitar',
      enableBiometricQuestion: '¿Desea habilitar',
      forFasterLogin: 'para iniciar sesión más rápido?',
      enableButton: 'Habilitar',
      skipButton: 'Omitir',
      biometricFailed: 'Autenticación biométrica fallida'
    }
  };

  const t = translations[language];

  const formatPhoneNumber = (value: string) => {
    const phoneNumber = value.replace(/\D/g, '');
    
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    } else {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
    setError('');
  };

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setPin(value);
    setError('');
  };

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'es' : 'en';
    setLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  // Biometric login handler
  const handleBiometricLogin = async () => {
    setLoading(true);
    setError('');

    try {
      const credentials = await biometricService.biometricLogin();
      if (credentials) {
        // Use stored credentials to login
        const response = await axios.post(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.LOGIN}`,
          {
            phone: credentials.phone,
            pin: credentials.pin
          }
        );

        if (response.data && response.data.success && response.data.data) {
          const { token, employee } = response.data.data;
          localStorage.setItem('bfa_token', token);
          localStorage.setItem('bfa_user', JSON.stringify(employee));

          if (employee.must_change_pin) {
            setEmployeeName(employee.first_name);
            setShowPinChange(true);
          } else {
            window.location.href = '/';
          }
        }
      }
    } catch (error: any) {
      console.log('Biometric login failed:', error);
      // Silently fail - user can still use PIN
    } finally {
      setLoading(false);
    }
  };

  // Enable biometrics handler
  const handleEnableBiometrics = async () => {
    if (!pendingCredentials) return;

    setLoading(true);
    const success = await biometricService.enable(
      pendingCredentials.phone,
      pendingCredentials.pin
    );

    if (success) {
      // Update status
      const status = await biometricService.getStatus();
      setBiometricStatus(status);
    }

    setShowBiometricPrompt(false);
    setPendingCredentials(null);
    setLoading(false);

    // Continue to app
    window.location.href = '/';
  };

  // Skip biometrics handler
  const handleSkipBiometrics = () => {
    setShowBiometricPrompt(false);
    setPendingCredentials(null);
    window.location.href = '/';
  };

  const handleNewPinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setNewPin(value);
    setError('');
  };

  const handleConfirmPinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setConfirmPin(value);
    setError('');
  };

  const handleSetNewPin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPin.length !== 4) {
      setError(t.invalidPin);
      return;
    }

    if (newPin !== confirmPin) {
      setError(t.pinMismatch);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('bfa_token');
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/api/auth/set-first-pin`,
        { newPin },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data && response.data.success) {
        // Update stored user to clear must_change_pin
        const storedUser = JSON.parse(localStorage.getItem('bfa_user') || '{}');
        storedUser.must_change_pin = false;
        localStorage.setItem('bfa_user', JSON.stringify(storedUser));

        // Redirect to home
        window.location.href = '/';
      } else {
        throw new Error(response.data?.error || 'Failed to set PIN');
      }
    } catch (error: any) {
      if (error.response) {
        setError(error.response.data?.error || 'Failed to set PIN');
      } else {
        setError(t.connectionError);
      }
    } finally {
      setLoading(false);
    }
  };

const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  // Trim whitespace from inputs (handles autofill issues)
  const cleanPhone = phone.trim();
  const cleanPin = pin.trim();

  const phoneDigits = cleanPhone.replace(/\D/g, '');
  if (phoneDigits.length !== 10) {
    setError(t.invalidPhone);
    return;
  }

  if (cleanPin.length !== 4) {
    setError(t.invalidPin);
    return;
  }

  setLoading(true);
  setError('');

  try {
    const response = await axios.post(
      `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.LOGIN}`,
      {
        phone: cleanPhone,
        pin: cleanPin
      }
    );

    if (response.data && response.data.success && response.data.data) {
      const { token, employee } = response.data.data;

      localStorage.setItem('bfa_token', token);
      localStorage.setItem('bfa_user', JSON.stringify(employee));

      // Check if user needs to change PIN
      if (employee.must_change_pin) {
        setEmployeeName(employee.first_name);
        setShowPinChange(true);
        setError('');
      } else {
        // Check if we should offer biometric setup
        if (biometricStatus?.isAvailable && !biometricStatus?.isEnabled) {
          setPendingCredentials({ phone: cleanPhone, pin: cleanPin });
          setShowBiometricPrompt(true);
        } else {
          window.location.href = '/';
        }
      }
    } else {
      throw new Error(response.data?.error || 'Login failed');
    }
  } catch (error: any) {
    if (error.response) {
      setError(error.response.data?.error || t.loginFailed);
    } else if (error.request) {
      setError(t.connectionError);
    } else {
      setError(t.loginFailed);
    }
  } finally {
    setLoading(false);
  }
};

  // Show PIN change screen
  if (showPinChange) {
    return (
      <div className="login-page">
        <button
          onClick={toggleLanguage}
          className="login-language-toggle"
          type="button"
        >
          {language === 'en' ? 'Español' : 'English'}
        </button>

        <div className="login-container">
          <div className="login-header">
            <h1>{t.welcomeUser}, {employeeName}!</h1>
            <p>{t.pinChangeRequired}</p>
          </div>

          <form onSubmit={handleSetNewPin} className="login-form">
            <div className="form-group">
              <label htmlFor="newPin">{t.newPinLabel}</label>
              <input
                id="newPin"
                name="newPin"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder={t.enterNewPin}
                value={newPin}
                onChange={handleNewPinChange}
                className="form-input"
                autoComplete="new-password"
                autoFocus
                maxLength={4}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPin">{t.confirmPinLabel}</label>
              <input
                id="confirmPin"
                name="confirmPin"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder={t.confirmNewPin}
                value={confirmPin}
                onChange={handleConfirmPinChange}
                className="form-input"
                autoComplete="new-password"
                maxLength={4}
                required
                disabled={loading}
              />
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="submit-button"
              disabled={loading || newPin.length !== 4 || confirmPin.length !== 4}
            >
              {loading ? t.settingPin : t.setPinButton}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Biometric enable prompt
  if (showBiometricPrompt && biometricStatus) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="login-header">
            <h1>{t.enableBiometric} {biometricStatus.biometryName}</h1>
            <p>
              {t.enableBiometricQuestion} {biometricStatus.biometryName} {t.forFasterLogin}
            </p>
          </div>

          <div className="biometric-prompt-buttons">
            <button
              onClick={handleEnableBiometrics}
              className="submit-button"
              disabled={loading}
            >
              {loading ? '...' : `${t.enableButton} ${biometricStatus.biometryName}`}
            </button>
            <button
              onClick={handleSkipBiometrics}
              className="skip-button"
              disabled={loading}
            >
              {t.skipButton}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Normal login screen
  return (
    <div className="login-page">
      <button
        onClick={toggleLanguage}
        className="login-language-toggle"
        type="button"
      >
        {language === 'en' ? 'Español' : 'English'}
      </button>

      <div className="login-container">
        <div className="login-header">
          <h1>Bradanna Field</h1>
          <p>{t.signIn}</p>
        </div>

        {/* Biometric quick login button */}
        {biometricStatus?.isEnabled && biometricStatus?.isAvailable && (
          <div className="biometric-login-section">
            <button
              onClick={handleBiometricLogin}
              className="biometric-button"
              disabled={loading}
              type="button"
            >
              <span className="biometric-icon">
                {biometricStatus.biometryName === 'Face ID' ? '👤' : '👆'}
              </span>
              {t.useBiometric} {biometricStatus.biometryName}
            </button>
            <div className="biometric-divider">
              <span>{t.orSignInWithPin}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form" autoComplete="off">
          <div className="form-group">
            <label htmlFor="phone">{t.phoneNumber}</label>
            <input
              id="phone"
              name="phone"
              type="tel"
              placeholder="XXX-XXX-XXXX"
              value={phone}
              onChange={handlePhoneChange}
              className="form-input"
              autoComplete="tel"
              autoFocus
              maxLength={12}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="pin">{t.pinLabel}</label>
            <input
              id="pin"
              name="pin"
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder={t.enterPin}
              value={pin}
              onChange={handlePinChange}
              className="form-input"
              autoComplete="current-password"
              maxLength={4}
              required
              disabled={loading}
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={loading || phone.replace(/\D/g, '').length !== 10 || pin.length !== 4}
          >
            {loading ? t.signingIn : t.signInButton}
          </button>
        </form>

        <div className="login-footer">
          <a
            href="#"
            onClick={async (e) => {
              e.preventDefault();
              const privacyUrl = 'https://icy-smoke-0fcf10a0f.2.azurestaticapps.net/privacy-policy.html';
              if (Capacitor.isNativePlatform()) {
                await Browser.open({ url: privacyUrl });
              } else {
                window.open(privacyUrl, '_blank');
              }
            }}
          >
            {language === 'en' ? 'Privacy Policy' : 'Política de Privacidad'}
          </a>
        </div>
      </div>
    </div>
  );
};